import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import type { UserDto, TokenDto, LoginUserDto, CreateUserDto } from '../types';
import { authApi } from '../api/auth.service';

interface AuthContextType {
  user: UserDto | null;
  loading: boolean;
  login: (credentials: LoginUserDto) => Promise<void>;
  register: (userData: CreateUserDto) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  updateUserBalance: (money: number, rouletteTokens?: number) => void;
  isAuthenticated: boolean;
  loginWithGoogle: () => void;
  loginWithYandex: () => void;
  loginWithVK: () => void;
  handleOAuthCallback: (provider: 'google' | 'yandex', code: string, redirectUri?: string, referralCode?: string, source?: string, deviceCode?: string) => Promise<void>;
  handleVKCallback: (code: string, codeVerifier: string, deviceId: string, state: string, redirectUri?: string, referralCode?: string, source?: string, deviceCode?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const userData = await authApi.getCurrentUser();
        setUser(userData);
      } catch (error) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      }
    }
    setLoading(false);
  };

  const login = useCallback(async (credentials: LoginUserDto) => {
    const tokens: TokenDto = await authApi.login(credentials);
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);

    const userData = await authApi.getCurrentUser();
    setUser(userData);
  }, []);

  const register = useCallback(async (userData: CreateUserDto) => {
    await authApi.register(userData);
    // Don't set user after registration - redirect to login instead
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authApi.getCurrentUser();
      setUser(userData);
    } catch {
      // Silently fail - user will need to re-login
    }
  }, []);

  const updateUserBalance = useCallback((money: number, rouletteTokens?: number) => {
    setUser(prev => {
      if (!prev) return null;
      const updatedUser = { ...prev, money };
      if (rouletteTokens !== undefined) {
        updatedUser.rouletteTokens = rouletteTokens;
      }
      return updatedUser;
    });
  }, []);

  const loginWithGoogle = useCallback(() => {
    authApi.loginWithGoogle();
  }, []);

  const loginWithYandex = useCallback(() => {
    authApi.loginWithYandex();
  }, []);

  const loginWithVK = useCallback(() => {
    authApi.loginWithVK();
  }, []);

  const handleOAuthCallback = useCallback(async (provider: 'google' | 'yandex', code: string, redirectUri?: string, referralCode?: string, source?: string, deviceCode?: string) => {
    const tokens: TokenDto = await authApi.handleOAuthCallback(provider, code, redirectUri, referralCode, source, deviceCode);
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);

    const userData = await authApi.getCurrentUser();
    setUser(userData);
  }, []);

  const handleVKCallback = useCallback(async (
    code: string,
    codeVerifier: string,
    deviceId: string,
    state: string,
    redirectUri?: string,
    referralCode?: string,
    source?: string,
    deviceCode?: string
  ) => {
    const tokens: TokenDto = await authApi.handleVKCallback(code, codeVerifier, deviceId, state, redirectUri, referralCode, source, deviceCode);
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);

    const userData = await authApi.getCurrentUser();
    setUser(userData);
  }, []);

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    login,
    register,
    logout,
    refreshUser,
    updateUserBalance,
    isAuthenticated: !!user,
    loginWithGoogle,
    loginWithYandex,
    loginWithVK,
    handleOAuthCallback,
    handleVKCallback,
  }), [user, loading, login, register, logout, refreshUser, updateUserBalance, loginWithGoogle, loginWithYandex, loginWithVK, handleOAuthCallback, handleVKCallback]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
